import { Label } from '../../../shared/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { editLabel, GPanelContactsKeys } from '../../../gpanel-contacts-api';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../../utils/roles/client-roles';
import axios from 'axios';
import { useState } from 'react';
import { ApiException } from '../../../../../../../types';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { Messages } from '../../../../../../utils/message-constants';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import { useCheckLabelNameCollision } from './use-check-label-collision';
import { useStore } from '../../../../../state/zustand-store';
import SharedDialog from '../../../../../../shared/shared-dialog';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export default function GpanelContactsEditLabelDialog({
  handleClose,
  label,
}: {
  handleClose: () => void;
  label: Label;
}) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const setSnackbar = useStore((state) => state.setSnackbar);
  const [errorText, setErrorText] = useState<string | null>(null);
  const { changeLogRoles } = useUserSettings();
  const [labelName, setLabelName] = useState(label.name);

  useCheckLabelNameCollision(labelName, label.name, setErrorText);

  const { mutate, isPending } = useMutation({
    mutationFn: () => editLabel(label.id, labelName, changeLogMessage),
    onSuccess: (data) => {
      setChangeLogMessage('');
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Label'),
      });
      queryClient.setQueryData([GPanelContactsKeys.LabelGroups], data.group);
      queryClient.setQueryData<Label[]>(
        [GPanelContactsKeys.GPanelLabels],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((l) =>
            l.id === label.id ? { ...l, name: labelName } : l
          );
        }
      );
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message) {
          setErrorText(errorResponse.message);
        } else {
          handleErrors({
            error,
            snackbarMessage: Messages.Errors.Network.Renaming('Label'),
          });
        }
      }
    },
    onSettled: () => {
      createEvent(analytics.directory.gpanel_contacts.update_label, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    const permission = GPanelContactsPermissions.EditGpanelLabels.value;

    if (hasPermission(changeLogRoles, permission)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <>
      <SharedDialog
        open
        handleClose={handleClose}
        title="Edit Label"
        dialogContent={
          <Stack gap={1}>
            <TextField
              label="Label Name"
              fullWidth
              autoFocus
              autoComplete="off"
              error={errorText !== null}
              helperText={errorText}
              value={labelName}
              onChange={(event) => setLabelName(event.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              This will change the label across all contacts assigned to it.
            </Typography>
          </Stack>
        }
        primaryButton={{
          title: 'Save Changes',
          action: handleSubmit,
          loading: isPending,
          disabled:
            labelName.length === 0 ||
            errorText !== null ||
            labelName.trim() === label.name,
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
